import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Navigation, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/auth/auth.service';
import { promiseWithCatch, unsubscribeAll } from 'src/app/common/app.helpers';
import { OperationInfoService } from 'src/app/common/long-operations/operation-info.service';
import { FormConstructorComponent } from 'src/app/forms/form-constructors/form-constructor/form-constructor.component';
import { FormStateService } from 'src/app/forms/form-state.service';
import { FormValidationService } from 'src/app/forms/form-validation.service';
import { FormConstructorContent } from 'src/app/interfaces/form-and-field.interface';
import { QueryUrlParams } from 'src/app/interfaces/query-url-params.interface';
import AppSettings from '../../common/app.settings';
import ProjectLinks from '../../common/project.links';
import { ChannelKind, OtpAuthenticatorStatus, OtpStatusResponse } from '../../swagger-api';
import { SecondAuthLevelService } from '../second-auth-level.service';
import { SessionService } from '../session.service';

@Component({
  selector: 'app-second-auth-level',
  templateUrl: './second-auth-level.component.html',
  styleUrls: ['./second-auth-level.component.scss'],
})
export class SecondAuthLevelComponent implements OnInit, OnDestroy {
  @ViewChild(FormConstructorComponent, { static: false }) public phoneForm: FormConstructorComponent;

  public formContent: FormConstructorContent;
  public initialValue = '';
  public changePhone: boolean;
  // Confirmed phone number from BE
  public storedPhoneNumber = '';
  public currentPhone = '';

  public isRequestCodeDisabled = false;
  public requestCodeBtnHint = '';
  public authenticatorTimerHint = '';

  public availableChannels: ChannelKind[] = [];
  public defaultAuthenticationChannel: ChannelKind;
  public selectedChannel: ChannelKind;

  public previousPage: string;

  public bindAuthenticator: boolean;
  public phoneNumber: string;
  public authenticatorStatus: OtpAuthenticatorStatus;
  public verificationAttemptsLimitReached: boolean;

  public isSmsChannelDisabled: boolean;
  public isAuthenticatorChannelDisabled: boolean;

  private readonly subscriptions$: Subscription[] = [];
  private urlParams: QueryUrlParams;

  private readonly securedPages: string[] = ['profile/view/:id'];

  private email = '';

  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly secondAuthLvlService = inject(SecondAuthLevelService);
  private readonly formStateService = inject(FormStateService);
  private readonly formValidationService = inject(FormValidationService);
  private readonly sessionService = inject(SessionService);
  private readonly operationInfoService = inject(OperationInfoService);
  private readonly translateService = inject(TranslateService);

  public codeControl: FormControl<string> = new FormControl('', this.secondAuthLvlService.verificationCodeValidators);

  public constructor() {
    this.setPreviousPage();
  }

  public ngOnInit(): void {
    this.check2AuthLvl();
  }

  public ngOnDestroy(): void {
    this.secondAuthLvlService.stopCodeRequestTimer();
    unsubscribeAll(this.subscriptions$);
  }

  public getValidationError(): string {
    return this.formValidationService.getValidationError(this.codeControl);
  }

  public isFieldInvalid(): boolean {
    return this.formValidationService.isInvalid(this.codeControl);
  }

  public sendCode(event: Event): void {
    if (!this.initialValue) {
      this.formStateService.stopAllAction(event);

      if (!this.phoneForm.childForm.isFormValid()) {
        return;
      }

      this.phoneForm.prepareSubmitData();
    }

    if (this.changePhone && this.formContent.data['phone_number'] === this.currentPhone) {
      this.secondAuthLvlService.showChangePhoneBeforeEditingMessage();
      return;
    }

    const sendCodeSuccessfulMessage: string = this.translateService.instant(
      'SECOND_AUTH_LVL_SUCCESS_CODE_SEND_DESCRIPTION',
      {
        contactEntry:
          this.selectedChannel === ChannelKind.Sms ? (this.formContent.data['phone_number'] as string) : this.email,
      },
    ) as string;

    this.operationInfoService.setupOperation(
      {
        spinner: 'button',
        button: { id: 'second-auth-lvl-send-btn' },
        successfulMessage: sendCodeSuccessfulMessage,
      },
      () => {
        this.secondAuthLvlService.sendCode(
          this.selectedChannel,
          this.initialValue.length === 0 ? (this.formContent.data['phone_number'] as string) : undefined,
        );
      },
    );
  }

  public verifyCode(event: Event): void {
    this.formStateService.stopAllAction(event);

    if (this.codeControl.invalid) {
      this.formStateService.validateControl(this.codeControl);
      return;
    }

    this.operationInfoService.setupOperation(
      {
        spinner: 'global',
        button: { id: 'second-auth-lvl-verify-code-btn' },
      },
      () => {
        this.updateUserPreferredChannel();

        this.secondAuthLvlService.verifyCode({
          code: this.codeControl.value,
          phoneNumber: this.initialValue.length === 0 ? (this.formContent.data['phone_number'] as string) : undefined,
          redirectPage: this.previousPage ? this.previousPage : '',
          channelKind: this.selectedChannel,
          params: this.urlParams,
          bindAuthenticator: this.bindAuthenticator,
        });
      },
    );
  }

  public cancel(): void {
    if ((this.changePhone || this.bindAuthenticator) && this.previousPage) {
      promiseWithCatch(this.router.navigateByUrl(this.previousPage));
      return;
    }

    if (this.isSecuredPage() || !this.previousPage) {
      promiseWithCatch(this.router.navigate([ProjectLinks.LINK_DEFAULT]));
      return;
    }

    this.authService.navigateByQueryParams();
  }

  public generateAuthenticatorSecret(): void {
    this.updateUserPreferredChannel();
    promiseWithCatch(
      this.router.navigate([ProjectLinks.BIND_AUTHENTICATOR], {
        state: {
          previousPage: ProjectLinks.LINK_SECOND_AUTH_LEVEL,
          email: this.email,
        },
      }),
    );
  }

  public onSelectedChannelType(value: ChannelKind): void {
    this.removeCodeControlValue();
    this.selectedChannel = value;
    if (value === ChannelKind.Email) {
      this.initialValue = this.email;
    } else if (value === ChannelKind.Sms) {
      // On tab change, only put the BE stored phone number,
      // If there was another phone number, it wasn't confirmed so it shouldn't be used
      this.initialValue = this.storedPhoneNumber;
    }

    this.changeDetectorRef.detectChanges();

    this.updateCodeControlStatus();
  }

  public onTabVisibilityChangesEventHandler(): void {
    if (!this.getDocumentVisibility()) {
      const otpRequestBlockedUntilString: string = JSON.parse(
        localStorage.getItem(AppSettings.LOCALSTORE_KEY_REQUEST_OTP_BLOCKED_UNTIL),
      ) as string;

      if (otpRequestBlockedUntilString) {
        this.secondAuthLvlService.setCodeRequestTimer(new Date(otpRequestBlockedUntilString));
      }
    }
  }

  private removeCodeControlValue(): void {
    this.codeControl.setValue('');
  }

  private isSecuredPage(): boolean {
    const [routePath, childPath, userId] = this.previousPage.split('/').filter(Boolean);
    const isSecuredPagesPresent = this.securedPages.some((page: string) => page === `${routePath}/${childPath}/:id`);

    return userId ? this.sessionService.userId == userId && isSecuredPagesPresent : isSecuredPagesPresent;
  }

  private getDocumentVisibility(): boolean {
    return document.hidden;
  }

  private check2AuthLvl(): void {
    if (this.sessionService.is2AuthLvlPassed && !this.changePhone && !this.bindAuthenticator) {
      promiseWithCatch(this.router.navigateByUrl(this.previousPage));
      return;
    }

    !this.sessionService.user
      ? this.subscriptions$.push(
          this.sessionService.sessionChanges.subscribe(() => {
            if (this.sessionService.user) {
              this.initForms();
            }
          }),
        )
      : this.initForms();
  }

  private setPreviousPage(): void {
    const navigation: Navigation = this.router.getCurrentNavigation();
    if (navigation?.extras.state?.['changePhone']) {
      this.changePhone = navigation.extras.state['changePhone'] as boolean;
    }
    if (navigation?.extras.state?.['server_action']) {
      this.urlParams = navigation.extras.state;
    }

    if (navigation?.extras.state?.['previousPage']) {
      this.previousPage = navigation.extras.state['previousPage'] as string;
    } else if (navigation?.extras.state?.['returnUrl']) {
      this.previousPage = navigation.extras.state['returnUrl'] as string;
    } else {
      this.previousPage = '/';
    }

    if (navigation?.extras.state?.['bindAuthenticator']) {
      this.bindAuthenticator = navigation.extras.state['bindAuthenticator'] as boolean;
    }
  }

  private initForms(): void {
    this.initPhoneControl();
    this.initRequestCodeBtn();
    this.initVerificationCodeControl();
    this.initChannelKindRadioBtn();
    this.subscribeOnTabVisibilityChanges();
  }

  private updateCodeControlStatus(): void {
    if (this.selectedChannel != ChannelKind.Authenticator) {
      this.codeControl.disable();
      return;
    }

    if ([OtpAuthenticatorStatus.Verified, OtpAuthenticatorStatus.Pending].includes(this.authenticatorStatus)) {
      this.codeControl.enable();
    } else {
      this.codeControl.disable();
    }
  }

  private initPhoneControl(): void {
    const subs: Subscription = this.secondAuthLvlService.secondAuthLvlContent.subscribe(
      (formContent: FormConstructorContent) => {
        this.formContent = formContent;
        this.email = formContent.data['email'] as string;
        this.phoneNumber = formContent.data['phone_number'] as string;
        if (this.changePhone) {
          this.currentPhone = this.formContent.data['phone_number'] as string;
        } else {
          const phoneNumber = this.formContent.data['phone_number'] as string;

          if (phoneNumber) {
            this.storedPhoneNumber = phoneNumber;
            this.initialValue = phoneNumber;
          }
        }
      },
    );

    this.subscriptions$.push(subs);
    this.secondAuthLvlService.fetchSecondFormAuthForm(this.changePhone);
  }

  private initVerificationCodeControl(): void {
    this.codeControl.disable();

    this.subscriptions$.push(
      this.secondAuthLvlService.codeSent.subscribe((sent: boolean) => {
        if (sent) {
          this.codeControl.enable();
        } else {
          this.codeControl.disable();
        }
      }),
    );
  }

  private initChannelKindRadioBtn(): void {
    this.subscriptions$.push(
      this.secondAuthLvlService.otpStatusResponse.subscribe((otpStatus: OtpStatusResponse) => {
        if (otpStatus.available_channels) {
          this.availableChannels = otpStatus.available_channels;
          this.setDefaultAuthenticationChannel();
        }

        if (otpStatus.authenticator_status) {
          this.authenticatorStatus = otpStatus.authenticator_status;
        }

        if (!this.changePhone) {
          this.selectedChannel = this.getUserPreferredChannelType();
          this.onSelectedChannelType(this.selectedChannel);
        } else {
          this.selectedChannel = ChannelKind.Sms;
        }

        this.setVerificationAttemptsLimitReached(otpStatus.verification_attempts_limit_reached);

        this.updateChannelsAvailability();

        if (otpStatus.current_otp_active_until) {
          this.codeControl.enable();
          return;
        }

        this.updateCodeControlStatus();
      }),
    );
  }

  private setDefaultAuthenticationChannel(): void {
    this.defaultAuthenticationChannel = this.availableChannels[0];
  }

  private setVerificationAttemptsLimitReached(isVerificationAttemptsLimitReached: boolean): void {
    this.verificationAttemptsLimitReached = isVerificationAttemptsLimitReached;
  }

  private updateChannelsAvailability(): void {
    this.isSmsChannelDisabled = !this.phoneNumber && this.authenticatorStatus == OtpAuthenticatorStatus.Verified;
    this.isAuthenticatorChannelDisabled =
      !!this.phoneNumber && this.authenticatorStatus != OtpAuthenticatorStatus.Verified;

    if (this.changePhone || this.bindAuthenticator) {
      return;
    }

    if (this.selectedChannel == ChannelKind.Authenticator && this.isAuthenticatorChannelDisabled) {
      this.selectedChannel = ChannelKind.Sms;
    }
    if (this.selectedChannel == ChannelKind.Sms && this.isSmsChannelDisabled) {
      this.selectedChannel = ChannelKind.Authenticator;
    }
  }

  private updateUserPreferredChannel(): void {
    const preferredChannelTypesByUserId: { [id: string]: ChannelKind } = this.getPreferredChannelTypes();
    preferredChannelTypesByUserId[this.sessionService.user.user_id] = this.selectedChannel;

    localStorage.setItem(
      AppSettings.LOCALSTORE_KEY_2FA_PREFERRED_CHANNEL,
      JSON.stringify(preferredChannelTypesByUserId),
    );
  }

  private getUserPreferredChannelType(): ChannelKind {
    if (this.bindAuthenticator) {
      return ChannelKind.Authenticator;
    }

    const preferredChannelTypesByUserId: { [id: string]: ChannelKind } = this.getPreferredChannelTypes();
    const preferredChannel: ChannelKind = preferredChannelTypesByUserId[this.sessionService.user.user_id];

    if (!this.checkPreferredChannelAvailability(preferredChannelTypesByUserId[this.sessionService.user.user_id])) {
      return this.defaultAuthenticationChannel;
    }

    return preferredChannel;
  }

  private getPreferredChannelTypes(): { [id: string]: ChannelKind } {
    const preferredUserChannelJSON: string = localStorage.getItem(AppSettings.LOCALSTORE_KEY_2FA_PREFERRED_CHANNEL);

    if (preferredUserChannelJSON) {
      const preferredUserChannel: { [id: string]: ChannelKind } = JSON.parse(preferredUserChannelJSON) as {
        [id: string]: ChannelKind;
      };

      if (this.sessionService.user.user_id in preferredUserChannel) {
        return preferredUserChannel;
      }
    }

    const resultObject: { [id: string]: ChannelKind } = {};
    resultObject[this.sessionService.user.user_id] = this.defaultAuthenticationChannel;
    return resultObject;
  }

  private checkPreferredChannelAvailability(channelKind: ChannelKind): boolean {
    // note: we need this only for users that already have preferredChannelType
    //       but by some reason it was disabled
    return this.availableChannels.includes(channelKind);
  }

  private initRequestCodeBtn(): void {
    this.requestCodeBtnHint = this.translateService.instant('SECOND_AUTH_LVL_SEND_CODE_HINT') as string;

    this.subscriptions$.push(
      this.secondAuthLvlService.codeCountdownTimer.subscribe((countNumber: number) => {
        this.isRequestCodeDisabled = countNumber > 1000;

        if (!this.isRequestCodeDisabled) {
          this.codeControl.disable();
          this.secondAuthLvlService.stopCodeRequestTimer();
        }

        const hours: number = Math.floor(countNumber / (1000 * 60 * 60));
        let remainingNumber: number = countNumber - hours * (1000 * 60 * 60);
        const minutes: number = Math.floor(remainingNumber / (1000 * 60));
        remainingNumber -= minutes * (1000 * 60);
        const seconds: number = Math.floor(remainingNumber / 1000);

        const hh: string = hours.toString().padStart(2, '0');
        const mm: string = minutes.toString().padStart(2, '0');
        const ss: string = seconds.toString().padStart(2, '0');

        this.requestCodeBtnHint = this.translateService.instant(
          countNumber > 1000 ? 'SECOND_AUTH_LVL_SEND_CODE_HINT_AVAILABLE_IN' : 'SECOND_AUTH_LVL_SEND_CODE_HINT',
          { countdown: +hh > 0 ? `${hh}:${mm}:${ss}` : `${mm}:${ss}` },
        ) as string;

        this.authenticatorTimerHint =
          countNumber > 1000
            ? (this.translateService.instant('SECOND_AUTH_LVL_TRY_AGAIN_IN_HINT', {
                countdown: +hh > 0 ? `${hh}:${mm}:${ss}` : `${mm}:${ss}`,
              }) as string)
            : '';
      }),

      this.secondAuthLvlService.verificationAttemptsLimitReached.subscribe(
        (isVerificationAttemptsLimitReached: boolean) => {
          this.setVerificationAttemptsLimitReached(isVerificationAttemptsLimitReached);
        },
      ),
    );
    this.secondAuthLvlService.getOtpStatus();
  }

  private subscribeOnTabVisibilityChanges(): void {
    window.addEventListener('visibilitychange', () => {
      this.onTabVisibilityChangesEventHandler();
    });
  }
}
